import React, { useState } from 'react';
import { useMaintenanceMode } from '../../hooks/useMaintenanceMode';
import styles from '../../styles/MaintenanceControl.module.css';

const MaintenanceControl: React.FC = () => {
  const {
    status,
    loading,
    error,
    isEnabled,
    canToggle,
    lastUpdated,
    enableMaintenance,
    disableMaintenance,
    refreshStatus,
    clearError,
  } = useMaintenanceMode(true); // Enable auto-refresh

  const [customMessage, setCustomMessage] = useState('');
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'enable' | 'disable' | null>(null);

  const handleToggleMaintenance = async (action: 'enable' | 'disable') => {
    setPendingAction(action);
    setShowConfirmDialog(true);
  };

  const confirmAction = async () => {
    if (!pendingAction) return;

    let success = false;
    
    if (pendingAction === 'enable') {
      success = await enableMaintenance(customMessage.trim() || undefined);
    } else {
      success = await disableMaintenance();
    }

    if (success) {
      setCustomMessage('');
      setShowMessageInput(false);
    }

    setShowConfirmDialog(false);
    setPendingAction(null);
  };

  const cancelAction = () => {
    setShowConfirmDialog(false);
    setPendingAction(null);
    setCustomMessage('');
    setShowMessageInput(false);
  };

  const formatUptime = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className={styles.maintenanceControl}>
      <div className={styles.maintenanceHeader}>
        <h2>🔧 Maintenance Mode Control</h2>
        <button 
          onClick={refreshStatus}
          disabled={loading}
          className="btn btn-secondary btn-small"
          title="Refresh status"
        >
          {loading ? '↻' : '🔄'}
        </button>
      </div>

      {error && (
        <div className={styles.maintenanceError}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorText}>{error}</span>
          <button onClick={clearError} className={styles.errorClose}>×</button>
        </div>
      )}

      <div className={styles.maintenanceStatus}>
        <div className={styles.statusIndicator}>
          <div className={`${styles.statusDot} ${isEnabled ? styles.enabled : styles.disabled}`}></div>
          <span className={styles.statusText}>
            {isEnabled ? 'MAINTENANCE ACTIVE' : 'SYSTEM OPERATIONAL'}
          </span>
        </div>

        {status && (
          <div className={styles.statusDetails}>
            {isEnabled && status.message && (
              <div className={styles.currentMessage}>
                <strong>Message:</strong> {status.message}
              </div>
            )}
            
            {lastUpdated && (
              <div className={styles.lastUpdated}>
                <small>
                  Last updated: {lastUpdated.toLocaleTimeString()}
                  {isEnabled && status?.timestamp && (
                    <span> • Active for: {formatUptime(status.timestamp)}</span>
                  )}
                </small>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.maintenanceControls}>
        {!isEnabled ? (
          <div className={styles.enableControls}>
            <button
              onClick={() => handleToggleMaintenance('enable')}
              disabled={!canToggle}
              className={`btn ${styles.btnWarning} ${styles.btnLarge}`}
            >
              🚨 Enable Maintenance Mode
            </button>
            
            <button
              onClick={() => setShowMessageInput(!showMessageInput)}
              className="btn btn-secondary"
              disabled={!canToggle}
            >
              {showMessageInput ? 'Cancel' : 'Custom Message'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleToggleMaintenance('disable')}
            disabled={!canToggle}
            className={`btn ${styles.btnSuccess} ${styles.btnLarge}`}
          >
            ✅ Disable Maintenance Mode
          </button>
        )}
      </div>

      {showMessageInput && (
        <div className={styles.messageInputSection}>
          <label htmlFor="maintenance-message">Custom Maintenance Message:</label>
          <textarea
            id="maintenance-message"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Enter a custom message for users (optional)"
            maxLength={500}
            rows={3}
            className={styles.maintenanceMessageInput}
          />
          <div className={styles.messageControls}>
            <small>{customMessage.length}/500 characters</small>
            <button
              onClick={() => handleToggleMaintenance('enable')}
              disabled={!canToggle}
              className="btn btn-primary"
            >
              Enable with Message
            </button>
          </div>
        </div>
      )}

      {status?.allowedRoutes && status.allowedRoutes.length > 0 && (
        <div className={styles.allowedRoutes}>
          <h3>Allowed Routes During Maintenance:</h3>
          <ul>
            {status.allowedRoutes.map((route, index) => (
              <li key={index}><code>{route}</code></li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={styles.maintenanceModalOverlay}>
          <div className={styles.maintenanceModal}>
            <h3>
              {pendingAction === 'enable' ? '🚨 Enable Maintenance?' : '✅ Disable Maintenance?'}
            </h3>
            <p>
              {pendingAction === 'enable' 
                ? 'This will block all game-related requests. Only admin and essential services will remain accessible.'
                : 'This will restore normal operation and allow all user requests.'
              }
            </p>
            
            {pendingAction === 'enable' && (
              <div className={styles.confirmMessage}>
                <strong>Message:</strong> {customMessage.trim() || 'Default maintenance message will be used'}
              </div>
            )}

            <div className={styles.modalActions}>
              <button onClick={cancelAction} className="btn btn-secondary">
                Cancel
              </button>
              <button 
                onClick={confirmAction} 
                className={`btn ${pendingAction === 'enable' ? 'btn-warning' : 'btn-success'}`}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceControl;